import { message } from 'antd';
import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Typography } from 'tfc-components';

import Icon from 'components/atoms/Icon';
import Loading from 'components/atoms/Loading';
import Section from 'components/organisms/Section';
import { IAlePaySuccesResponnse } from 'services/accounts/types';
import {
  addNewFaildService, addNewSuccessService, changePlanRetryService, getOverViewService
} from 'services/overview';
import { ROUTE_PATHS } from 'utils/constants';
import ERROR_MESSAGES, { DEFAULT_ERROR } from 'utils/errors';
import mapModifiers from 'utils/functions';

const ChangePlanStatus: React.FC = () => {
  const { status } = useParams<{ status: string }>();
  const [searchParams] = useSearchParams();
  const [dataResponse, setDataResponse] = useState<IAlePaySuccesResponnse>();
  const {
    transactionCode, errorCode, cancel, merchantSideUserId, checksum, data, token
  } = Object.fromEntries(searchParams);
  const [retyToken, setRetyToken] = useState(token);

  const navigate = useNavigate();

  /* Queries */
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    data: overViewData,
  } = useQuery({
    queryKey: ['get-overview'],
    queryFn: getOverViewService,
    enabled: status === 'cancel'
  });

  const { mutate: handlePaymentSuccess, isLoading: isLoadingChecking } = useMutation(
    ['checkingPayment'],
    addNewSuccessService,
    {
      onSuccess(dataSuccess) {
        if (dataSuccess) {
          setDataResponse(dataSuccess);
        }
        localStorage.removeItem('paymentChecked');
      },
      onError(errors: any) {
        if (errors.length > 0) {
          errors.forEach((error: ErrorResponse) => {
            message.error(ERROR_MESSAGES[error.code] || DEFAULT_ERROR);
            if ((error.details as any)?.token) {
              setRetyToken((error.details as any)?.token);
            }
          });
        } else {
          message.error('Something went wrong, please try again later');
        }
        const params = new URLSearchParams();
        if (token) params.append('token', token);
        if (data) params.append('data', data);
        if (checksum) params.append('checksum', checksum);
        if (errorCode) params.append('errorCode', errorCode);
        if (cancel) params.append('cancel', cancel);
        if (transactionCode) params.append('transactionCode', transactionCode);
        if (merchantSideUserId) params.append('merchantSideUserId', merchantSideUserId);

        navigate(`${ROUTE_PATHS.CHANGE_PLAN}/payment-status/cancel`);
        // }
      }
    }
  );

  const { mutate: handleCheckingPaymentCancel, isLoading: isLoadingCheckingCancel } = useMutation(
    ['checkingPaymentCancel'],
    addNewFaildService,
    {
      onSuccess() {
      },
      onError(error: any) {
        message.error(error.response.message);
      },
    }
  );

  const { mutate: handleChangePlanRetry } = useMutation(
    ['retry-change-plan'],
    changePlanRetryService,
    {
      onSuccess(dataRetry) {
        navigate(ROUTE_PATHS.CHANGE_PLAN, {
          state: { ...overViewData, dataRetry }
        });
      },
      onError(error: any) {
        message.error(error.response.message);
      },
    }
  );

  useEffect(() => {
    if (status === 'success') {
      handlePaymentSuccess({
        errorCode,
        transactionCode,
        cancel,
        merchantSideUserId,
        checksum,
        data,
        token
      });
    } else if (token) {
      handleCheckingPaymentCancel({
        errorCode,
        transactionCode,
        cancel,
        merchantSideUserId,
        checksum,
        data,
        token
      });
    }
  }, [transactionCode, merchantSideUserId, errorCode,
    cancel, handleCheckingPaymentCancel, status, handlePaymentSuccess, checksum, data, token]);

  const handleNavigate = () => {
    // const paymentChecked = localStorage.getItem('paymentChecked');
    if (status === 'success') {
      // localStorage.removeItem('paymentChecked');
      message.success('Plan changed successfully');
      return navigate('/billing');
    }
    // if (paymentChecked) {
    //   return navigate(`${ROUTE_PATHS.OVER_VIEW}`);
    // }
    return handleChangePlanRetry(retyToken);
    // return navigate(ROUTE_PATHS.CHANGE_PLAN, { state: { billingData: overViewData } });
  };

  return isLoadingChecking || isLoadingCheckingCancel ? <div className="p-checkout_paymentStatus_loading"><Loading /></div> : (
    <Section className="p-checkout_paymentStatus">
      <div className="p-checkout_paymentStatus_container">
        <div className={mapModifiers('p-checkout_paymentStatus_icon', status === 'success' && 'success')}>
          <Icon iconName={status === 'success' ? 'checkWhite' : 'warningWhite'} size="30" />
        </div>
        <div className="p-checkout_paymentStatus_wrap">
          <Typography.Text fontweight="600" extendClasses="p-checkout_paymentStatus_text fs-24x30">
            {status === 'success' ? 'Payment successful' : 'Payment failed'}
          </Typography.Text>
          <Typography.Text extendClasses="p-checkout_paymentStatus_description fs-18x21">
            {(() => {
              if (status === 'success') {
                return (
                  <>
                    Your payment has been processed successfully. A confirmation email has
                    <br />
                    been sent to your email
                    {' '}
                    {dataResponse?.email}
                  </>
                );
              }
              return (
                <>
                  Oops! Something went wrong with your payment.
                  <br />
                  Try again or contact support.
                </>
              );
            })()}
          </Typography.Text>
        </div>
        <div className={mapModifiers('p-checkout_paymentStatus_buttonWrap', status === 'cancel' && 'failed')}>
          {status !== 'success' && <Button disabled={isLoadingChecking || isLoadingCheckingCancel} extendClasses="p-checkout_paymentStatus_button p-checkout_paymentStatus_buttonContact">Contact support</Button>}
          <Button disabled={isLoadingChecking || isLoadingCheckingCancel} onClick={() => handleNavigate()} variant="primary" extendClasses="p-checkout_paymentStatus_button">{status === 'success' ? 'Back to Billing page' : 'Try again'}</Button>
        </div>
      </div>
    </Section>
  );
};

export default ChangePlanStatus;
